import { useNavigate } from "react-router-dom";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminOrders } from "@/hooks/useAdminOrders";
import type { GetOrdersParams } from "@/services/admin-order.service";
import { OrderListTable } from "./OrderListTable";

interface EntityOrdersTabProps {
  tenantId?: string;
  userId?: string;
}

export function EntityOrdersTab({ tenantId, userId }: EntityOrdersTabProps) {
  const navigate = useNavigate();
  // Page 1 of 10, fixed: the table used to take an `onPageChange` it never called, so this state
  // only ever held 1. Kept as a constant rather than as state that cannot change - a pager here is
  // a separate piece of work.
  const params: GetOrdersParams = {
    page: 1,
    pageSize: 10,
    tenantId,
    userId,
  };
  const { data, isLoading, isFetching, error, refetch } = useAdminOrders(params);

  // Only take the card over when there is genuinely nothing to show. A background refetch that
  // fails while rows are already on screen used to replace the whole card with the full-screen
  // fallback, throwing away rows that were still valid a moment earlier.
  if (error && !data) {
    return (
      <ErrorFallback
        error={error as Error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Orders</CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-2 flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
            <p className="text-sm text-destructive-ink">
              Could not refresh the list. Showing the last result that loaded.
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              disabled={isFetching}
              className="h-7 shrink-0 px-2 text-xs"
            >
              Retry
            </Button>
          </div>
        )}
        {/* The table owns its own skeleton rows, so the tab no longer swaps a centred spinner in
            and out — which changed the card's height on every load. */}
        <OrderListTable
          orders={data?.data ?? []}
          pagination={data?.pagination}
          onViewDetails={(orderId) => navigate(`/admin/orders/${orderId}`)}
          showActions={false}
          isLoading={isLoading}
        />
      </CardContent>
    </Card>
  );
}
